/**
 * "View as participant" — the nested level of a support session
 * (SUPPORT-IMPERSONATION). Only reachable while already impersonating a
 * tenant_admin (the button that opens it is gated on that). Assumes a
 * participant of the current tenant so a support agent can see exactly
 * what the participant sees. Read-only is inherited from the tenant_admin
 * session (a read-only parent forces a read-only child; the backend
 * enforces this too).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import { listParticipants, type Participant } from '../api/tenants';
import { extractErrorMessage } from '../api/errors';
import { useImpersonation } from '../auth/ImpersonationContext';

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
}

/**
 * Onboarding-status glyph for the participant picker: a support agent
 * often wants to open a *pending* participant precisely to see their
 * empty "we're ready for your data" dashboard, so the list flags who has
 * actually signed in (has_signed_in) vs who's still just invited.
 */
function ParticipantStatusIcon({ signedIn }: { signedIn: boolean }) {
  return signedIn ? (
    <Tooltip title="Active — has signed in">
      <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
    </Tooltip>
  ) : (
    <Tooltip title="Pending — invited, hasn't signed in yet">
      <HourglassEmptyIcon fontSize="small" sx={{ color: 'warning.main' }} />
    </Tooltip>
  );
}

const participantName = (p: Participant) =>
  p.full_name || (p.has_email ? p.email : 'Participant');

export function ViewAsParticipantDialog({
  open,
  onClose,
  tenantId,
  tenantName,
}: Props) {
  const { enter, readOnly: parentReadOnly } = useImpersonation();
  const navigate = useNavigate();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [targetId, setTargetId] = useState('');
  const [readOnly, setReadOnly] = useState(true);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setTargetId('');
    setReason('');
    setReadOnly(parentReadOnly || true);
    void listParticipants(tenantId)
      .then((ps) => setParticipants(ps.filter((p) => p.is_active)))
      .catch(() => setError('Could not load participants.'));
  }, [open, tenantId, parentReadOnly]);

  // Alphabetical by the name shown, so a long roster is scannable; the
  // picker is also type-to-filter.
  const sortedParticipants = useMemo(
    () =>
      [...participants].sort((a, b) =>
        participantName(a).localeCompare(participantName(b), undefined, {
          sensitivity: 'base',
          numeric: true,
        }),
      ),
    [participants],
  );
  const target = participants.find((p) => p.id === targetId) ?? null;
  const canStart = Boolean(target) && reason.trim().length > 0 && !busy;

  const handleStart = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    const ro = parentReadOnly || readOnly;
    try {
      // Nested: no tenant_id — the backend infers the nested level from the
      // current act-as session and keeps it in the same tenant.
      await enter({
        targetUserId: target.id,
        readOnly: ro,
        reason: reason.trim(),
        level: {
          role: 'participant',
          label: participantName(target),
          tenantName,
          readOnly: ro,
        },
      });
      onClose();
      navigate('/dashboard');
    } catch (e) {
      setError(extractErrorMessage(e, 'Could not start the participant view.'));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>View as participant</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            See exactly what this participant sees. You stay inside the same
            support session — exit drops you back to the tenant admin.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Autocomplete<Participant, false, false, false>
            fullWidth
            options={sortedParticipants}
            value={target}
            onChange={(_e, p) => setTargetId(p?.id ?? '')}
            getOptionLabel={participantName}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText="No active participants"
            autoHighlight
            renderOption={(props, p) => {
              const { key, ...rest } = props as typeof props & { key: string };
              return (
                <li key={`${key}-${p.id}`} {...rest}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ width: '100%' }}
                  >
                    <ParticipantStatusIcon signedIn={p.has_signed_in} />
                    <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                      {participantName(p)}
                    </Box>
                    {!p.has_signed_in && (
                      <Chip
                        size="small"
                        label="Pending"
                        color="warning"
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    )}
                  </Stack>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="View as participant" />
            )}
          />
          <FormControlLabel
            control={
              <Switch
                checked={parentReadOnly || readOnly}
                disabled={parentReadOnly}
                onChange={(e) => setReadOnly(e.target.checked)}
              />
            }
            label={
              parentReadOnly
                ? 'Read-only (inherited from this session)'
                : readOnly
                  ? 'Read-only (recommended)'
                  : 'Read-write'
            }
          />
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            multiline
            minRows={2}
            fullWidth
            helperText="Required — stored on the audit trail."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleStart()}
          disabled={!canStart}
        >
          {busy ? 'Starting…' : 'View as participant'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
